import { ActionIcon, Avatar, Group, Menu, Popover, Stack, Text, Tooltip } from "@mantine/core";
import {
  Calendar,
  Download,
  Ellipsis,
  Info,
  Lightbulb,
  Megaphone,
  Pencil,
  RefreshCw,
  SmilePlus,
  Trash2,
  WandSparkles,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { downloadSignedFile } from "../../../api/download";
import { useListMembers } from "../../../api/generated/endpoints/teams/teams";
import type { AiActor } from "../../../api/generated/model";
import { memberName } from "../../../platform/members";
import { formatDay } from "../../../ui/format";
import { MediaCard } from "../../../ui/MediaCard";
import { Soon } from "../../../ui/Soon";
import { ThumbImage } from "../../../ui/ThumbImage";
import classes from "./actors.module.css";

export interface MyActorCardProps {
  teamId: string;
  actor: AiActor;
  /** The signed-in user may delete it (its maker, an admin or the owner); else no trash. */
  mayDelete: boolean;
  onDelete: () => void;
  onPreview: () => void;
  /** Live's "Create a Talking Head": the Studio's Talking Head with this actor. */
  onTalkingHead: () => void;
}

/** The file's name to save under: the actor's, with its picture's extension. */
function fileOf(actor: AiActor): string {
  const extension = new URL(actor.image_url).pathname.split(".").pop() ?? "png";
  return `${actor.name}.${extension}`;
}

/**
 * Live's My Actors tile (capture L04 to L04c): the portrait at its shape; on hover live's dark
 * pill with the trash, the info and "…". The info says who made it and when; "…" holds live's
 * seven items, Download working and the rest Coming soon until their screens. The card opens
 * the portrait in the shared lightbox (issue 828, bug 25).
 */
export function MyActorCard(props: MyActorCardProps) {
  const { teamId, actor, mayDelete, onDelete, onPreview, onTalkingHead } = props;
  const menu = (
    <Group gap={4} wrap="nowrap">
      {mayDelete ? (
        <Tooltip label="Delete">
          <ActionIcon variant="text-white" aria-label={`Delete ${actor.name}`} onClick={onDelete}>
            <Trash2 size={20} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      <ActorInfo teamId={teamId} actor={actor} />
      <ActorMenu actor={actor} onTalkingHead={onTalkingHead} trigger={{ variant: "text-white" }} />
    </Group>
  );
  return (
    <MediaCard menu={menu}>
      <button
        type="button"
        className={classes.portrait}
        style={{ aspectRatio: `${actor.width} / ${actor.height}` }}
        aria-label={actor.name}
        onClick={onPreview}
      >
        {/* The card's portrait: the 512 px one, else the portrait itself (issue 828, item 12). */}
        <ThumbImage thumb={actor.thumb_url} full={actor.image_url} alt="" className={classes.media} />
      </button>
    </MediaCard>
  );
}

/**
 * Live's "…" of a team actor (`useAvatarCardActions`), on its card and in its preview: Create a
 * Talking Head, Download, then live's five items Coming soon until their screens.
 */
export function ActorMenu(props: {
  actor: AiActor;
  onTalkingHead: () => void;
  /** The trigger's look: the card's dark pill, or the preview's bar. */
  trigger: { variant: string; c?: string; color?: string };
}) {
  const { actor, onTalkingHead } = props;
  return (
    <Menu position="bottom-start" withinPortal>
      <Menu.Target>
        <ActionIcon {...props.trigger} aria-label={`More for ${actor.name}`}>
          <Ellipsis size={20} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<SmilePlus size={16} />} onClick={onTalkingHead}>
          Create a Talking Head
        </Menu.Item>
        <Menu.Item
          leftSection={<Download size={16} />}
          onClick={() => void downloadSignedFile(actor.image_url, fileOf(actor)).catch(() => undefined)}
        >
          Download
        </Menu.Item>
        <SoonItem icon={<Lightbulb size={16} />} label="Mark as Inspiration" />
        <SoonItem icon={<RefreshCw size={16} />} label="Re-index" />
        <SoonItem icon={<Pencil size={16} />} label="Edit" />
        <SoonItem icon={<WandSparkles size={16} />} label="Animate" />
        <SoonItem icon={<Megaphone size={16} />} label="Upload as ad" />
      </Menu.Dropdown>
    </Menu>
  );
}

/** A menu item whose screen is not built yet: live's place and words, "Coming soon". */
function SoonItem({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <Soon block>
      {(soon) => (
        <Menu.Item {...soon} closeMenuOnClick={false} leftSection={icon}>
          {label}
        </Menu.Item>
      )}
    </Soon>
  );
}

/** Live's info popover (L04c): "CREATED BY" with the maker's initials and name, then "DATE". */
function ActorInfo({ teamId, actor }: { teamId: string; actor: AiActor }) {
  const [open, setOpen] = useState(false);
  // The members are read when the info first opens; live shows "Unknown" without a maker.
  const members = useListMembers(teamId, { query: { enabled: open } }).data;
  const maker = members?.find((member) => member.user_id === actor.created_by);
  const name = memberName(maker);
  return (
    <Popover opened={open} onChange={setOpen} position="bottom-start" shadow="md">
      <Popover.Target>
        <ActionIcon
          variant="text-white"
          aria-label={`About ${actor.name}`}
          aria-expanded={open}
          onClick={() => setOpen((now) => !now)}
        >
          <Info size={20} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown aria-label={`About ${actor.name}`}>
        <Stack gap={8}>
          <Text className={classes.infoTitle}>CREATED BY</Text>
          <Group gap={8} wrap="nowrap">
            <Avatar size={24} radius="xl" name={name} color="indigo" />
            <Text size="sm">{name}</Text>
          </Group>
          <Text className={classes.infoTitle}>DATE</Text>
          <Group gap={8} wrap="nowrap">
            <Calendar size={16} aria-hidden />
            <Text size="sm">{formatDay(actor.created_at)}</Text>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
